import isEmpty from "lodash-es/isEmpty";
import {
  BulkActionCount,
  DisplayFieldErrors,
  InputField,
  EfsMelcoEfsModelsFormat,
  EfsMelcoEfsModelsMappingCondition,
  useTableSelection,
} from "melco-shared-logic";
import {
  BulkActionContainerCol,
  Button,
  DesignTokens,
  FullWidthSpace,
  PlusIcon,
  Space,
  Table,
  TableActionBarRow,
  Title,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useDesignMappingDataAssignments } from "../../hooks/designMappings/useDesignMappingDataAssignments";
import { useProcessingConditions } from "../../hooks/designMappings/useProcessingConditions";
import { useAdvancedOptions } from "../../hooks/settings/useAdvancedOptions";
import { EmptyWrapper } from "../shared/EmptyWrapper";
import { BulkActionRemoveProcessingConditions } from "./BulkActionRemoveProcessingConditions";
import { ProcessingConditionCustomTokenField } from "./ProcessingConditionCustomTokenField";
import { SelectOperator } from "./SelectOperator";
import { SelectProcessingConditionDataSource } from "./SelectProcessingConditionDataSource";

const indexAsRowKey = (_: unknown, idx: number | undefined) => idx!.toString();

const { spacings } = DesignTokens;

const TableWrapper = styled.div`
  margin-left: -${spacings.xlarge}px;
  margin-right: -${spacings.xlarge}px;
`;

const TableInputFieldWrapper = styled.div`
  margin-bottom: -${spacings.middle}px;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

type ProcessingConditionsProps = {
  inputFormat: EfsMelcoEfsModelsFormat | undefined;
};

export const ProcessingConditions: React.FC<ProcessingConditionsProps> = ({
  inputFormat,
}) => {
  const { t } = useTranslation();

  const { processingConditions, addProcessingCondition } =
    useProcessingConditions();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection<EfsMelcoEfsModelsMappingCondition>(() => {});

  const dataAssignments = useDesignMappingDataAssignments(inputFormat);

  const isAdvancedOptionsEnabled = useAdvancedOptions();

  const columns = [
    {
      title: t(
        "designMappings.actions.processingConditions.list.columns.format_detail_id"
      ),
      key: "format_detail_id",
      dataIndex: "format_detail_id",
      render: (
        _: string,
        __: EfsMelcoEfsModelsMappingCondition,
        idx: number
      ) => {
        return (
          <TableInputFieldWrapper>
            <SelectProcessingConditionDataSource
              name={`conditions[${idx}].format_detail_id`}
              dataAssignments={dataAssignments}
            />
          </TableInputFieldWrapper>
        );
      },
    },
    {
      title: t(
        "designMappings.actions.processingConditions.list.columns.operator"
      ),
      key: "operator",
      dataIndex: "operator",
      render: (
        _: string,
        __: EfsMelcoEfsModelsMappingCondition,
        idx: number
      ) => (
        <TableInputFieldWrapper>
          <SelectOperator name={`conditions[${idx}].operator`} />
        </TableInputFieldWrapper>
      ),
    },
    {
      title: t(
        "designMappings.actions.processingConditions.list.columns.values"
      ),
      key: "values",
      dataIndex: "values",
      render: (
        _: string,
        __: EfsMelcoEfsModelsMappingCondition,
        idx: number
      ) => (
        <TableInputFieldWrapper>
          <InputField name={`conditions[${idx}].values`} />
        </TableInputFieldWrapper>
      ),
    },
  ];

  if (isAdvancedOptionsEnabled) {
    columns.push({
      title: t(
        "designMappings.actions.processingConditions.list.columns.xpath_token"
      ),
      key: "xpath_token",
      dataIndex: "xpath_token",
      render: (
        _: string,
        __: EfsMelcoEfsModelsMappingCondition,
        idx: number
      ) => (
        <ProcessingConditionCustomTokenField
          idx={idx}
          dataAssignments={dataAssignments}
        />
      ),
    });
  }

  const addButton = (
    <Button type="primary" icon={<PlusIcon />} onClick={addProcessingCondition}>
      {t("designMappings.actions.processingConditions.actions.add.button")}
    </Button>
  );

  return (
    <>
      <Title level={5}>
        {t("designMappings.detail.form.processingConditions.title")}
      </Title>

      <DisplayFieldErrors name="conditions" aggregateErrors />

      {isEmpty(processingConditions) ? (
        <FullWidthSpace direction="vertical">
          <EmptyWrapper data-cy-id="processing-conditions-empty-wrapper">
            {addButton}
          </EmptyWrapper>
        </FullWidthSpace>
      ) : (
        <Space direction="vertical">
          <TableWrapper>
            <TableActionBarRow>
              <BulkActionContainerCol>
                <BulkActionCount selectedCount={selectedRowKeys.length} />
                <BulkActionRemoveProcessingConditions
                  selectedRowKeys={selectedRowKeys}
                  setSelectedRowKeys={setSelectedRowKeys}
                />
              </BulkActionContainerCol>
            </TableActionBarRow>
            <Table
              showSorterTooltip={false}
              sticky
              dataSource={processingConditions ?? []}
              columns={columns}
              rowKey={indexAsRowKey}
              rowSelection={rowSelection}
              onRow={onRow}
              pagination={false}
              data-cy-id="processing-conditions-table"
            />
          </TableWrapper>

          <AddButtonWrapper>{addButton}</AddButtonWrapper>
        </Space>
      )}
    </>
  );
};
